import { useCallback, useEffect, useState } from "react";
import { toast } from "react-toastify";
import {
  PiBriefcaseLight,
  PiBuildingsLight,
  PiCheckCircleLight,
  PiClockLight,
  PiMapPinLight,
  PiMoneyLight,
  PiPencilSimpleLineLight,
} from "react-icons/pi";
import client from "../../api/client";
import FeatureFeedbackBox from "../../components/Generic/FeatureFeedbackBox";

const REGIONS = [
  { value: "nigeria", label: "Nigeria" },
  { value: "africa", label: "Africa (outside Nigeria)" },
  { value: "uk", label: "United Kingdom" },
  { value: "us", label: "United States" },
  { value: "remote_global", label: "Remote / Global" },
];

const REMOTE_PREFS = [
  { value: "any", label: "No preference" },
  { value: "remote", label: "Remote" },
  { value: "hybrid", label: "Hybrid" },
  { value: "onsite", label: "Onsite" },
];

const SENIORITIES = [
  { value: "any", label: "No preference" },
  { value: "entry", label: "Entry level" },
  { value: "mid", label: "Mid level" },
  { value: "senior", label: "Senior" },
];

// Display names for job sources. Himalayas' terms require naming it as the source.
const SOURCE_LABELS = {
  e1x: "Posted by E1X Academy",
  jobberman: "Jobberman",
  myjobmag: "MyJobMag",
  hotnigerianjobs: "HotNigerianJobs",
  jobzilla: "Jobzilla",
  brightermonday: "BrighterMonday",
  careers24: "Careers24",
  himalayas: "via Himalayas",
  jsearch: "JSearch",
  linkedin: "LinkedIn",
  adzuna: "Adzuna",
  reed: "Reed",
};

const EMPLOYMENT_LABELS = {
  full_time: "Full-time",
  part_time: "Part-time",
  contract: "Contract",
  internship: "Internship",
};

const REMOTE_BADGE = {
  remote: "bg-emerald-50 text-emerald-700 border-emerald-100",
  hybrid: "bg-amber-50 text-amber-700 border-amber-100",
  onsite: "bg-gray-50 text-gray-600 border-gray-200",
  unknown: "bg-gray-50 text-gray-500 border-gray-200",
};

function formatSalary(posting) {
  if (!posting.salary_min && !posting.salary_max) return null;
  const currency = posting.salary_currency || "";
  const fmt = (n) => Number(n).toLocaleString();
  if (posting.salary_min && posting.salary_max) {
    return `${currency} ${fmt(posting.salary_min)} – ${fmt(posting.salary_max)}`;
  }
  return `${currency} ${fmt(posting.salary_min || posting.salary_max)}`;
}

function formatPostedAgo(dateString) {
  if (!dateString) return null;
  const posted = new Date(dateString);
  if (Number.isNaN(posted.getTime())) return null;

  const diffMs = Date.now() - posted.getTime();
  const hours = Math.floor(diffMs / 3_600_000);

  if (hours < 1) return "Just now";
  if (hours < 24) return `${hours}hr${hours === 1 ? "" : "s"} ago`;

  const days = Math.floor(hours / 24);
  if (days < 7) return `${days} day${days === 1 ? "" : "s"} ago`;

  const weeks = Math.floor(days / 7);
  if (weeks < 5) return `${weeks} week${weeks === 1 ? "" : "s"} ago`;

  const months = Math.floor(days / 30);
  return `${months} month${months === 1 ? "" : "s"} ago`;
}

function labelFor(options, value) {
  return options.find((o) => o.value === value)?.label || value;
}

function PreferencesForm({ categories, initialValues, onSaved, onCancel }) {
  const [form, setForm] = useState({
    career_path: initialValues?.career_path || "",
    category: initialValues?.category || "",
    region: initialValues?.region || "nigeria",
    remote_pref: initialValues?.remote_pref || "any",
    seniority_target: initialValues?.seniority_target || "any",
  });
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.career_path) {
      toast.error("Please choose a career path.");
      return;
    }

    setSubmitting(true);
    try {
      const token = localStorage.getItem("nd-rest-tkn");
      if (token) client.defaults.headers.Authorization = `Bearer ${token}`;

      const res = await client.post("/me/job-preferences", form);
      toast.success("Preferences saved — finding your matches…");
      onSaved(res.data);
    } catch (err) {
      const msg = err?.response?.data?.message || "Could not save your preferences.";
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="bg-white rounded-2xl border border-gray-200 shadow-sm p-6">
      <div className="flex items-center gap-2 text-base font-semibold text-gray-800 mb-1">
        <PiBriefcaseLight size={20} />
        {initialValues ? "Edit your preferences" : "Tell us what you're looking for"}
      </div>
      <p className="text-sm text-gray-500 mb-5">
        A few quick preferences help us match you to relevant postings.
      </p>

      <div className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1.5">Career path</label>
          <select
            value={form.career_path}
            onChange={(e) => setForm((prev) => ({ ...prev, career_path: e.target.value }))}
            className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm text-gray-700
              focus:outline-none focus:ring-2 focus:ring-[#F05A19]/30 focus:border-[#F05A19]"
          >
            <option value="">Select a career path</option>
            {categories.map((c) => (
              <option key={c.id} value={c.name}>{c.name}</option>
            ))}
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1.5">
            Specific role <span className="text-gray-400 font-normal">(optional)</span>
          </label>
          <input
            type="text"
            value={form.category}
            onChange={(e) => setForm((prev) => ({ ...prev, category: e.target.value }))}
            placeholder="e.g. Backend Engineer, Data Analyst"
            className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm text-gray-700
              focus:outline-none focus:ring-2 focus:ring-[#F05A19]/30 focus:border-[#F05A19]"
          />
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">Region</label>
            <select
              value={form.region}
              onChange={(e) => setForm((prev) => ({ ...prev, region: e.target.value }))}
              className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm text-gray-700
                focus:outline-none focus:ring-2 focus:ring-[#F05A19]/30 focus:border-[#F05A19]"
            >
              {REGIONS.map((r) => (
                <option key={r.value} value={r.value}>{r.label}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">Work style</label>
            <select
              value={form.remote_pref}
              onChange={(e) => setForm((prev) => ({ ...prev, remote_pref: e.target.value }))}
              className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm text-gray-700
                focus:outline-none focus:ring-2 focus:ring-[#F05A19]/30 focus:border-[#F05A19]"
            >
              {REMOTE_PREFS.map((r) => (
                <option key={r.value} value={r.value}>{r.label}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">Seniority</label>
            <select
              value={form.seniority_target}
              onChange={(e) => setForm((prev) => ({ ...prev, seniority_target: e.target.value }))}
              className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm text-gray-700
                focus:outline-none focus:ring-2 focus:ring-[#F05A19]/30 focus:border-[#F05A19]"
            >
              {SENIORITIES.map((s) => (
                <option key={s.value} value={s.value}>{s.label}</option>
              ))}
            </select>
          </div>
        </div>
      </div>

      <div className="mt-5 flex items-center gap-3">
        <button
          type="submit"
          disabled={submitting}
          className="px-5 py-2.5 rounded-xl text-sm font-semibold
            bg-[#F05A19] hover:bg-[#d94e14] text-white transition-colors disabled:opacity-60"
        >
          {submitting ? "Saving…" : "Save preferences"}
        </button>
        {onCancel && (
          <button
            type="button"
            onClick={onCancel}
            disabled={submitting}
            className="px-5 py-2.5 rounded-xl text-sm font-semibold text-gray-600 hover:bg-gray-50 transition-colors"
          >
            Cancel
          </button>
        )}
      </div>
    </form>
  );
}

function PreferencesSummary({ preferences, onEdit }) {
  return (
    <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-4 mb-6 flex items-center justify-between flex-wrap gap-3">
      <div className="flex flex-wrap items-center gap-2 text-sm text-gray-600">
        <span className="font-semibold text-gray-800">{preferences.career_path}</span>
        {preferences.category && <span>· {preferences.category}</span>}
        <span>· {labelFor(REGIONS, preferences.region)}</span>
        <span>· {labelFor(REMOTE_PREFS, preferences.remote_pref)}</span>
      </div>
      <button
        onClick={onEdit}
        className="inline-flex items-center gap-1.5 text-sm font-semibold text-[#F05A19] hover:text-[#d94e14] transition-colors"
      >
        <PiPencilSimpleLineLight size={16} />
        Edit preferences
      </button>
    </div>
  );
}

// `match` is the student's own match ({ id, match_score }) when there is one — browsing
// can surface jobs that aren't matched, which then open without click tracking.
function JobCard({ posting, match }) {
  const [clicking, setClicking] = useState(false);
  if (!posting) return null;

  const salary = formatSalary(posting);
  const postedAgo = formatPostedAgo(posting.posted_at);
  const remoteBadge = REMOTE_BADGE[posting.remote_type] || REMOTE_BADGE.unknown;

  const handleViewAndApply = async () => {
    if (!match) {
      window.open(posting.source_url, "_blank", "noopener,noreferrer");
      return;
    }
    setClicking(true);
    try {
      const token = localStorage.getItem("nd-rest-tkn");
      if (token) client.defaults.headers.Authorization = `Bearer ${token}`;
      await client.post(`/me/job-matches/${match.id}/click`);
    } catch {
      // Non-blocking — still let the student get to the listing even if the tracking call fails.
    } finally {
      setClicking(false);
      window.open(posting.source_url, "_blank", "noopener,noreferrer");
    }
  };

  return (
    <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-5 flex flex-col h-full">
      <div className="flex items-start justify-between gap-3 mb-2">
        <h3 className="text-base font-semibold text-gray-800 leading-snug">{posting.title}</h3>
        {match && (
          <span className="shrink-0 inline-flex items-center gap-1 bg-orange-50 text-[#F05A19] text-xs font-bold px-2.5 py-1 rounded-full">
            {match.match_score}% match
          </span>
        )}
      </div>

      <div className="flex flex-wrap items-center gap-x-4 gap-y-1.5 text-sm text-gray-500 mb-3">
        {posting.company && (
          <span className="inline-flex items-center gap-1.5">
            <PiBuildingsLight size={15} />
            {posting.company}
          </span>
        )}
        {posting.location && (
          <span className="inline-flex items-center gap-1.5">
            <PiMapPinLight size={15} />
            {posting.location}
          </span>
        )}
      </div>

      {salary && (
        <div className="flex items-center gap-1.5 text-sm text-gray-500 mb-3">
          <PiMoneyLight size={15} />
          {salary}
        </div>
      )}

      <div className="flex items-center gap-2 mb-3 flex-wrap">
        <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium border ${remoteBadge}`}>
          {posting.remote_type === "unknown" ? "Work style unspecified" : posting.remote_type}
        </span>
        {posting.employment_type && EMPLOYMENT_LABELS[posting.employment_type] && (
          <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium border bg-gray-50 text-gray-600 border-gray-200">
            {EMPLOYMENT_LABELS[posting.employment_type]}
          </span>
        )}
        <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium border ${
          posting.source === "e1x" ? "bg-orange-50 text-[#F05A19] border-orange-100" : "bg-gray-50 text-gray-600 border-gray-200"
        }`}>
          {SOURCE_LABELS[posting.source] || posting.source}
        </span>
      </div>

      {postedAgo && (
        <div className="flex items-center gap-1.5 text-xs text-gray-400 mb-4">
          <PiClockLight size={14} />
          Posted {postedAgo}
          {posting.expires_at && (
            <span>· Closes {new Date(posting.expires_at).toLocaleDateString(undefined, { day: "numeric", month: "short" })}</span>
          )}
        </div>
      )}

      <button
        onClick={handleViewAndApply}
        disabled={clicking}
        className="mt-auto w-full flex items-center justify-center gap-1.5 bg-[#F05A19] text-white text-sm
          font-medium py-2.5 rounded-lg hover:bg-[#d94e14] transition-colors disabled:opacity-60"
      >
        View &amp; Apply
      </button>
    </div>
  );
}

const FOR_YOU = "for_you";
const ALL = "all";

function CategoryTabs({ tabs, active, onChange }) {
  return (
    <div className="flex gap-2 overflow-x-auto pb-2 mb-6 -mx-4 px-4 md:mx-0 md:px-0 md:flex-wrap">
      {tabs.map((tab) => (
        <button
          key={tab.key}
          onClick={() => onChange(tab.key)}
          className={`shrink-0 inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium border transition-colors ${
            active === tab.key
              ? "bg-[#F05A19] text-white border-[#F05A19]"
              : "bg-white text-gray-600 border-gray-200 hover:border-gray-300"
          }`}
        >
          {tab.label}
          {tab.count != null && (
            <span className={`text-xs ${active === tab.key ? "text-white/80" : "text-gray-400"}`}>{tab.count}</span>
          )}
        </button>
      ))}
    </div>
  );
}

function EmptyState({ title, text }) {
  return (
    <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-8 text-center">
      <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
        <PiCheckCircleLight size={30} className="text-gray-400" />
      </div>
      <h2 className="text-base font-semibold text-gray-800 mb-1">{title}</h2>
      <p className="text-sm text-gray-500 max-w-sm mx-auto">{text}</p>
    </div>
  );
}

/** Every live job in one career path (or all), paginated with "Load more". */
function BrowseJobs({ careerPath, onCounts }) {
  const [jobs, setJobs] = useState([]);
  const [meta, setMeta] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async (page) => {
    setLoading(true);
    try {
      const res = await client.get("/me/jobs", {
        params: { page, ...(careerPath !== ALL ? { career_path: careerPath } : {}) },
      });
      const paged = res.data?.data;
      setJobs((prev) => (page === 1 ? paged?.data || [] : [...prev, ...(paged?.data || [])]));
      setMeta(paged);
      if (res.data?.counts) onCounts(res.data.counts);
    } catch {
      toast.error("Could not load jobs.");
    } finally {
      setLoading(false);
    }
  }, [careerPath, onCounts]);

  useEffect(() => {
    setJobs([]);
    load(1);
  }, [load]);

  if (loading && jobs.length === 0) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
        {[1, 2, 3].map((n) => (
          <div key={n} className="bg-white rounded-2xl border border-gray-100 p-5 h-56 animate-pulse" />
        ))}
      </div>
    );
  }

  if (jobs.length === 0) {
    return <EmptyState title="No jobs here yet" text="We add new postings several times a week. Try another category or check back soon." />;
  }

  return (
    <>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
        {jobs.map((job) => <JobCard key={job.id} posting={job} match={job.match} />)}
      </div>
      {meta && meta.current_page < meta.last_page && (
        <div className="flex justify-center mt-6">
          <button
            onClick={() => load(meta.current_page + 1)}
            disabled={loading}
            className="px-5 py-2.5 rounded-lg border border-gray-200 bg-white text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-60"
          >
            {loading ? "Loading…" : `Load more (${meta.total - jobs.length} left)`}
          </button>
        </div>
      )}
    </>
  );
}

export default function JobBoardPage() {
  const [state, setState] = useState({ loading: true, eligible: true, needsPreferences: false, matches: [] });
  const [categories, setCategories] = useState([]);
  const [preferences, setPreferences] = useState(null);
  const [editing, setEditing] = useState(false);
  const [activeTab, setActiveTab] = useState(FOR_YOU);
  const [counts, setCounts] = useState(null);

  const refresh = useCallback(async () => {
    const token = localStorage.getItem("nd-rest-tkn");
    if (token) client.defaults.headers.Authorization = `Bearer ${token}`;

    try {
      const res = await client.get("/me/job-matches");
      setState({
        loading: false,
        eligible: res.data.eligible ?? true,
        needsPreferences: res.data.needs_preferences ?? false,
        matches: res.data.data || [],
      });
    } catch {
      toast.error("Could not load the job board.");
      setState((prev) => ({ ...prev, loading: false }));
    }

    try {
      const prefRes = await client.get("/me/job-preferences");
      setPreferences(prefRes.data?.data || null);
    } catch {
      // Non-blocking — the summary bar just won't show if this fails.
    }
  }, []);

  useEffect(() => {
    if (!state.eligible || state.loading) return;
    client.get("/me/jobs", { params: { page: 1 } })
      .then((res) => res.data?.counts && setCounts(res.data.counts))
      .catch(() => {});
  }, [state.eligible, state.loading]);

  useEffect(() => {
    refresh();
    client.get("/v2/categories").then((res) => setCategories(res.data || [])).catch(() => {});
  }, [refresh]);

  const handlePreferencesSaved = (payload) => {
    setPreferences(payload.data);
    setEditing(false);
    refresh();
  };

  return (
    <div className="py-8 px-4 md:px-8 max-w-6xl mx-auto">
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-gray-900 tracking-tight">Job Board</h1>
        <p className="text-sm text-gray-500 mt-1">
          Curated postings from across the web. See your matches, or browse every job by career path.
        </p>
      </div>

      {state.loading ? (
        <div className="bg-white rounded-2xl border border-gray-100 p-10 animate-pulse">
          <div className="h-4 bg-gray-200 rounded w-1/3 mb-3" />
          <div className="h-3 bg-gray-100 rounded w-2/3" />
        </div>
      ) : !state.eligible ? (
        <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-8 text-center">
          <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <PiBriefcaseLight size={30} className="text-gray-400" />
          </div>
          <h2 className="text-base font-semibold text-gray-800 mb-1">Job Board isn't available yet</h2>
          <p className="text-sm text-gray-500 max-w-sm mx-auto">
            The Job Board is available to fully-paid students during their cohort and for 30 days
            after it ends. Complete your payment or check back once your cohort starts.
          </p>
        </div>
      ) : state.needsPreferences || editing ? (
        <PreferencesForm
          categories={categories}
          initialValues={editing ? preferences : null}
          onSaved={handlePreferencesSaved}
          onCancel={editing ? () => setEditing(false) : null}
        />
      ) : (
        <>
          {preferences && <PreferencesSummary preferences={preferences} onEdit={() => setEditing(true)} />}

          <CategoryTabs
            active={activeTab}
            onChange={setActiveTab}
            tabs={[
              { key: FOR_YOU, label: "For you", count: state.matches.length },
              { key: ALL, label: "All jobs", count: counts?.all },
              ...Object.keys(counts || {})
                .filter((key) => key !== ALL)
                .map((key) => ({ key, label: key, count: counts[key] })),
            ]}
          />

          {activeTab === FOR_YOU ? (
            state.matches.length === 0 ? (
              <EmptyState
                title="No matches yet"
                text="We add new postings several times a week. Browse the other categories in the meantime — your preferences are saved."
              />
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
                {state.matches.map((match) => (
                  <JobCard key={match.id} posting={match.job_posting} match={match} />
                ))}
              </div>
            )
          ) : (
            <BrowseJobs careerPath={activeTab} onCounts={setCounts} />
          )}

          <div className="mt-6">
            <FeatureFeedbackBox feature="job_board" />
          </div>
        </>
      )}
    </div>
  );
}
